/**
 * 活动总结组件
 * 支持预生成、确认保存、查看、删除总结
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  SpinLoading, 
  DotLoading,
  Toast, 
  Divider,
  TextArea
} from 'antd-mobile';
import { 
  FileOutline,
} from 'antd-mobile-icons';
import { getZoneApi } from '../../api';
import './EventSummary.css';

/**
 * 简单的Markdown格式化（转换为HTML）
 * 支持基本的Markdown语法（标题/段落/列表/分割线/代码块/行内格式）
 *
 * 说明：
 * - LLM 输出不可控，先做 escape 再做最小可用的 markdown 解析，避免插入 HTML 破坏布局
 */
const formatMarkdown = (text) => {
  if (!text) return '';

  const escapeHtml = (value) => {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const formatInline = (value) => {
    let s = value;
    // 行内 code：`code`
    s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
    // 粗体：**text**
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // 斜体：*text*（尽量避免与粗体冲突）
    s = s.replace(/(^|[^*])\*([^*]+)\*([^*]|$)/g, '$1<em>$2</em>$3');
    return s;
  };

  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  let htmlParts = [];

  let inCodeBlock = false;
  let codeLines = [];
  let inList = false;
  let listTag = 'ul';
  let paragraphLines = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const paragraph = paragraphLines.join(' ').trim();
    if (paragraph) {
      htmlParts.push(`<p>${formatInline(paragraph)}</p>`);
    }
    paragraphLines = [];
  };

  const closeList = () => {
    if (!inList) return;
    htmlParts.push(`</${listTag}>`);
    inList = false;
    listTag = 'ul';
  };

  const openList = (tag) => {
    if (inList && listTag === tag) return;
    if (inList) closeList();
    listTag = tag;
    inList = true;
    htmlParts.push(`<${listTag}>`);
  };

  for (const rawLine of lines) {
    const line = escapeHtml(rawLine);
    const trimmed = line.trim();

    // 代码块 ``` ... ```
    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        htmlParts.push(
          `<pre><code>${codeLines.join('\n')}</code></pre>`
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        flushParagraph();
        closeList();
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    // 空行：结束段落/列表（但保留结构）
    if (!trimmed) {
      flushParagraph();
      closeList();
      continue;
    }

    // 分割线：--- / *** / ___
    if (/^([-*_])\1\1+$/.test(trimmed)) {
      flushParagraph();
      closeList();
      htmlParts.push('<hr />');
      continue;
    }

    // 标题：# / ## / ###
    const hMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const content = formatInline(hMatch[2].trim());
      flushParagraph();
      closeList();
      htmlParts.push(`<h${level}>${content}</h${level}>`);
      continue;
    }

    // 有序列表：1. xxx
    const olMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      openList('ol');
      htmlParts.push(`<li>${formatInline(olMatch[1].trim())}</li>`);
      continue;
    }

    // 无序列表：- xxx / * xxx
    const ulMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      flushParagraph();
      openList('ul');
      htmlParts.push(`<li>${formatInline(ulMatch[1].trim())}</li>`);
      continue;
    }

    // 普通段落行
    closeList();
    paragraphLines.push(trimmed);
  }

  // 收尾
  if (inCodeBlock) {
    htmlParts.push(`<pre><code>${codeLines.join('\n')}</code></pre>`);
  }
  flushParagraph();
  closeList();

  return htmlParts.join('\n');
};

/**
 * 活动总结组件
 * 
 * @param {Object} props
 * @param {String} props.eventId - 活动ID
 * @param {String} props.zone - 区域代码
 * @param {Boolean} props.isSettled - 活动是否已结算
 * @param {Function} props.onSummaryUpdate - 总结更新回调（可选）
 */
const EventSummary = ({
  eventId,
  zone,
  isSettled = false,
  onSummaryUpdate
}) => {
  const [summaryState, setSummaryState] = useState('none'); // 'none' | 'generating' | 'preview' | 'saved' | 'error'
  const [savedSummary, setSavedSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showExtraPromptModal, setShowExtraPromptModal] = useState(false);
  const [extraPrompt, setExtraPrompt] = useState('');

  const extraPromptStorageKey = `aiSummaryExtraPrompt:${zone}:${eventId}`;

  // 组件挂载时获取已保存的总结
  useEffect(() => {
    if (eventId && zone) {
      fetchSavedSummary();
    }
  }, [eventId, zone]);

  // 读取本地缓存的“现场补充信息”
  useEffect(() => {
    if (!eventId || !zone) return;
    const cached = sessionStorage.getItem(extraPromptStorageKey) || '';
    setExtraPrompt(cached);
  }, [eventId, zone, extraPromptStorageKey]);

  const persistExtraPrompt = (value) => {
    if (!eventId || !zone) return;
    sessionStorage.setItem(extraPromptStorageKey, value || '');
  };

  /**
   * 获取已保存的总结
   */
  const fetchSavedSummary = async () => {
    if (!eventId || !zone) return;

    try {
      setLoading(true);
      const zoneApi = getZoneApi(zone);
      const response = await zoneApi.events.summary.get(eventId);

      if (response.status === 'success') {
        if (response.data) {
          setSavedSummary(response.data);
          setSummaryState('saved');
        } else {
          setSavedSummary(null);
          setSummaryState('none');
        }
        setError(null);
      }
    } catch (err) {
      console.error('获取总结失败:', err);
      setError(err.response?.data?.message || '获取总结失败');
      setSummaryState('error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 生成并保存总结（跳过预览）
   */
  const handleGenerateAndSave = async () => {
    console.log('handleGenerateAndSave 被调用', { eventId, zone, isSettled });
    
    if (!eventId || !zone) {
      console.error('缺少必要参数', { eventId, zone });
      Toast.show({ 
        content: '缺少必要参数', 
        position: 'center' 
      });
      return;
    }

    if (!isSettled) {
      console.log('活动未结算');
      Toast.show({ 
        content: '活动尚未结算，无法生成总结', 
        position: 'center' 
      });
      return;
    }

    try {
      console.log('开始生成并保存总结...');
      setSummaryState('generating');
      setLoading(true);
      setError(null);

      const zoneApi = getZoneApi(zone);
      console.log('调用API:', { zone, eventId, zoneApi });
      const response = await zoneApi.events.summary.confirm(eventId, {
        extraPrompt: (extraPrompt || '').trim()
      });
      console.log('API响应:', response);

      if (response.status === 'success') {
        setSavedSummary(response.data);
        setSummaryState('saved');
        Toast.show({ 
          content: '总结已生成并保存', 
          position: 'center',
          duration: 2000
        });

        if (onSummaryUpdate) {
          onSummaryUpdate(response.data);
        }
      } else {
        throw new Error(response.message || '生成总结失败');
      }
    } catch (err) {
      console.error('生成总结失败:', err);
      const errorMsg = err.response?.data?.message || err.message || '生成总结失败';
      setError(errorMsg);
      setSummaryState('error');
      Toast.show({ 
        content: errorMsg, 
        position: 'center',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const openExtraPromptModal = () => {
    if (!isSettled || loading) return;
    setShowExtraPromptModal(true);
  };

  // 已保存总结的删除/重新生成入口已移到“活动管理”列表中，详情页仅展示内容

  // 渲染内容区域
  const renderContent = () => {
    if (loading && summaryState === 'generating') {
      return (
        <div className="event-summary-loading">
          <SpinLoading />
          <p>正在生成总结，请稍候...</p>
          <p className="loading-hint">这可能需要10-30秒</p>
        </div>
      );
    }

    if (summaryState === 'saved' && savedSummary) {
      return (
        <div className="event-summary-saved">
          <div className="summary-header">
            <span className="saved-badge">已保存</span>
            <span className="summary-meta">
              {savedSummary.model} · {savedSummary.tokensUsed} tokens
            </span>
          </div>
          <Divider />
          <div className="summary-content">
            <div className="markdown-content" dangerouslySetInnerHTML={{ __html: formatMarkdown(savedSummary.content) }} />
          </div>
        </div>
      );
    }

    if (summaryState === 'error') {
      return (
        <div className="event-summary-error">
          <p className="error-message">{error || '生成总结失败'}</p>
          <Button 
            onClick={handleGenerateAndSave}
            disabled={!isSettled}
            color="primary"
            block
          >
            <FileOutline /> 重试
          </Button>
        </div>
      );
    }

    // 默认状态：未生成
    return (
      <div className="event-summary-empty">
        <FileOutline className="empty-icon" />
        <p className="empty-text">尚未生成活动总结</p>
        <Button 
          onClick={(e) => {
            console.log('生成总结按钮被点击', { eventId, zone, isSettled, loading });
            e.preventDefault();
            e.stopPropagation();
            openExtraPromptModal();
          }}
          disabled={!isSettled || loading}
          color="primary"
          block
        >
          {loading ? <DotLoading /> : <FileOutline />}
          {loading ? '生成中...' : '生成并保存总结'}
        </Button>
        {!isSettled && (
          <p className="empty-hint">活动结算后才能生成总结</p>
        )}
      </div>
    );
  };

  return (
    <Card className="event-summary-card" title="活动总结">
      {renderContent()}

      {showExtraPromptModal && (
        <div className="event-summary-modal-overlay">
          <div className="event-summary-modal">
            <div className="event-summary-modal-header">
              <div className="event-summary-modal-title">
                现场补充信息（可选）
              </div>
              <button
                className="event-summary-modal-close"
                onClick={() => setShowExtraPromptModal(false)}
                aria-label="关闭"
              >
                ×
              </button>
            </div>

            <div className="event-summary-modal-body">
              <div className="event-summary-modal-hint">
                可以写：场地/天气、趣事梗、球员特点、想重点夸/调侃谁等
              </div>
              <TextArea
                value={extraPrompt}
                onChange={(val) => {
                  setExtraPrompt(val);
                  persistExtraPrompt(val);
                }}
                placeholder="例如：A今天刚复出状态爆棚；B喜欢网前搓球但容易失误；现场气氛很燃..."
                autoSize={{ minRows: 5, maxRows: 10 }}
                maxLength={800}
                showCount
              />
            </div>

            <div className="event-summary-modal-actions">
              <Button
                fill="outline"
                onClick={() => setShowExtraPromptModal(false)}
              >
                取消
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  setShowExtraPromptModal(false);
                  handleGenerateAndSave();
                }}
              >
                开始生成
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default EventSummary;
